// MIDAS V3 — Chat area (Phase 2)
//
// Replaces the Phase 1 placeholder body. Layout per spec §1:
//   • Centered MachinePill at the top of the centre column
//   • Scrollable message list (max-width 820, centered)
//   • Composer pinned at the bottom
//
// Reads `state.chatBot.messages` via the existing v2 selector pattern. Uses
// the same `MIDAS-SCOPESTREAM` thread-buffer reads so streaming is scoped to
// the active thread (the popup/MIDAS reconciliation logic in
// `chatBotSlice.sendMessage` is unchanged).
//
// Initial bot-state hydration on mount mirrors v2's `MidasChatAreaV2`:
//   • checkBotAccess + checkHealth
//   • fetchHistory if there's no active thread
//   • fetchThreads
//   • appendPendingBotMessage on popup adoption + active-thread stream sync
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  appendPendingBotMessage,
  checkBotAccess,
  checkHealth,
  fetchHistory,
} from "../../../../shared/chatBot/chatBotSlice";
import { POPUP_THREAD_ID } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { fetchThreads, loadThread } from "../../midasPageSlice";
import { restoreAttachedThreads } from "../../midasAttachSlice";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { Machine } from "../../../machinery/models/models";
import { BREAKPOINTS } from "../../../../shared/hooks/useBreakpoint";
import ChatInputAreaV3 from "./ChatInputAreaV3";
import ChatMessageListV3 from "./ChatMessageListV3";
import MachinePillV3 from "./MachinePillV3";
import MultiChatRowV3 from "./attach/MultiChatRowV3";
import { isCcDevSurface } from "./ccSurface";

// MIDAS-V3-BUGS-0519-R6 sub-task 4 helper — flatten the customerMachinery
// machines map into a flat array to test whether machines have been loaded.
// Kept in-file (same as MidasV3Layout's helper) to avoid a shared util for
// read-only guard logic.
const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

const MidasChatAreaV3: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const user = useAppSelector((state) => state.account.user);
  const activeThreadId = useAppSelector(
    (state) => state.midasPage.activeThreadId
  );
  const threadDetailLoading = useAppSelector(
    (state) => state.midasPage.threadDetailLoading
  );
  const isBotAvailable = useAppSelector(
    (state) => state.chatBot.isBotAvailable
  );
  // MIDAS-V3-BUGS-0519-R6 sub-task 4 — guard selectors for the rehydration
  // sequence below. Read-only inside the mount effect's lazy-state lookup.
  const machineContext = useAppSelector(
    (state) => state.chatBot.machineContext
  );
  const machinesMap = useAppSelector(
    (state) => state.customerMachinery.machines
  );
  const machineryLoading = useAppSelector(
    (state) => state.customerMachinery.loading
  );

  // MIDAS-SCOPESTREAM buffers — identical pattern to v2 MidasChatAreaV2.
  const effectiveThreadId = activeThreadId || POPUP_THREAD_ID;
  const threadBuffer = useAppSelector(
    (s) => s.chatBot.threadBuffers[effectiveThreadId]
  );
  const popupBuffer = useAppSelector(
    (s) => s.chatBot.threadBuffers[POPUP_THREAD_ID]
  );

  // Initial bot-state hydration on mount.
  //
  // MIDAS-V3-BUGS-0519-R6 sub-task 4 — async-sequenced rehydration so an F5
  // reload restores the active thread's machine context (composer pill) and
  // conversation messages from the bot's session-stored state.
  //
  // Cause chain that R6 closes:
  //   1. F5 → Redux resets → `activeThreadId = null` AND `machineContext = null`.
  //   2. The bot's session STILL knows the last-active thread (it returns its
  //      messages via `/v1/bot/chat/history`) but the frontend never dispatches
  //      `loadThread` on mount to grab the canonical full-fidelity context.
  //   3. `fetchHistory.fulfilled` restores only a degraded 3-field
  //      machineContext ({ machineId, model, serialNumber }) — missing the
  //      manufacturer / type / axis-config / control-system / production-year
  //      fields the pill needs for a full rendering.
  //
  // Path C from the spec — server-driven rehydration:
  //   • Step 1: ensure `customerMachinery.machines` is loaded (sub-task 3 lifts
  //     this to layout level, but we add a defensive guard here in case this
  //     component mounts before the layout's effect fires; the slice's own
  //     thunk de-dupes via the redux-toolkit pending state).
  //   • Step 2: dispatch `fetchHistory()` + `fetchThreads()` (existing calls).
  //   • Step 3: if `activeThreadId` is still null AND `machineContext` is null
  //     AND `fetchThreads` returned at least one thread, dispatch
  //     `loadThread(threads[0].id)`. The thunk reads `detail.machine_id` and
  //     dispatches `setMachineContextDirect` with the FULL machine payload
  //     (via `findMachineInStore` against the now-loaded `customerMachinery`
  //     slice). Thread order from the server is `updated_at DESC` so
  //     `threads[0]` is the most-recently-active.
  //
  // Skipped when: machineContext is already non-null (warm load, no rehydration
  // needed), no threads returned (first-time user — fresh state UI is correct),
  // or `loadThread` rejects (existing `.rejected` handler kicks in — silent
  // failure → user sees fresh state).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    dispatch(checkBotAccess());
    dispatch(checkHealth());

    const rehydrate = async () => {
      try {
        // Step 1 — ensure machines are loaded so `loadThread` can resolve the
        // full machine record via `findMachineInStore`. Guard prevents redundant
        // fetch when layout-level prefetch (sub-task 3) already loaded them.
        if (
          flattenMachines(machinesMap).length === 0 &&
          !machineryLoading
        ) {
          await dispatch(getMachines({})).unwrap();
        }
        if (cancelled) return;

        // Step 2 — existing dispatches. `fetchHistory` is only safe to call
        // when no thread is active and not currently loading (mirrors v2's
        // gating). `fetchThreads` is the source of `threads[0]` for step 3.
        if (!activeThreadId && !threadDetailLoading) {
          dispatch(fetchHistory());
        }
        const threadsResult = await dispatch(fetchThreads()).unwrap();
        if (cancelled) return;

        // Step 3 — F5 rehydration. Only when:
        //   • no thread is currently active (cold mount, not warm nav),
        //   • machineContext is null (degraded fetchHistory partial restore
        //     hasn't already populated it),
        //   • at least one thread exists on the server (no rehydration target
        //     otherwise → first-time user, leave fresh state).
        const mostRecentThread = threadsResult?.threads?.[0];
        if (
          !activeThreadId &&
          !machineContext &&
          mostRecentThread?.id
        ) {
          // Fire-and-forget — existing `.rejected` handler in midasPageSlice
          // covers thread-deleted / session-expired failures silently.
          dispatch(loadThread(mostRecentThread.id));
        }
      } catch {
        // Swallow rehydration errors — the existing slice-level `.rejected`
        // handlers manage user-facing state (`fetchHistory` 401, `loadThread`
        // 404, etc.). Failing the rehydration just leaves the user in
        // fresh-state UI, which is acceptable.
      }
    };

    rehydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  // Popup adoption: if a stream finished in the popup while we mounted here,
  // commit the pending bot message into the popup thread.
  useEffect(() => {
    if (popupBuffer && !popupBuffer.isLoading && popupBuffer.pendingBotMessage) {
      dispatch(appendPendingBotMessage({ threadId: POPUP_THREAD_ID }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active-thread sync: when the stream completes on the current thread,
  // append its pending bot message.
  const hasPendingMsg = threadBuffer?.pendingBotMessage;
  useEffect(() => {
    if (
      activeThreadId &&
      threadBuffer &&
      !threadBuffer.isLoading &&
      threadBuffer.pendingBotMessage
    ) {
      dispatch(appendPendingBotMessage({ threadId: activeThreadId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, hasPendingMsg, dispatch]);

  // ── MODEL-PICKER Feature 2: attached columns (spec §6) ──
  const attachedColumns = useAppSelector(
    (s: any) => s.midasAttach?.columns ?? []
  ) as unknown[];

  // Restore persisted attachments on mount — same gate as the rail's
  // "Attach chat" item (`.cc` + desktop ≥1280px). Mount-only.
  useEffect(() => {
    if (
      isCcDevSurface() &&
      typeof window !== "undefined" &&
      window.innerWidth >= BREAKPOINTS.tablet
    ) {
      dispatch(restoreAttachedThreads());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primarySurface = (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Conversation header — centred MachinePill on its own row. */}
      <div
        style={{
          padding: "12px 24px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <MachinePillV3 />
      </div>

      {/* Bot-offline banner — minimal Phase 2 treatment. The full empty-state
          chrome with CloudOff lives in v2; v3 will reskin in a later phase. */}
      {isBotAvailable === false && (
        <div
          style={{
            padding: "8px 24px",
            background:
              "color-mix(in oklab, var(--red) 10%, var(--card))",
            color: "var(--red)",
            fontSize: 12,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
            textAlign: "center",
            borderBottom: "1px solid var(--border-soft)",
            flexShrink: 0,
          }}
        >
          {translate("unavailable", {
            defaultValue: "MIDAS is temporarily unavailable.",
          }) as string}
        </div>
      )}

      {/* Scroll area — owns its own internal max-width via the list. */}
      <ChatMessageListV3 threadId={activeThreadId || undefined} />

      {/* Composer — pinned at the bottom. */}
      <ChatInputAreaV3 />
    </div>
  );

  // No attachments → the EXACT single-chat view of today (no strip, no slim
  // headers, no hairlines — spec §11 detach-all criterion).
  if (attachedColumns.length === 0) {
    return primarySurface;
  }

  // ≥1 attachment → the multi-column strip. The primary surface mounts
  // UNCHANGED as the first column; welcome state (no active thread) renders
  // beside live attached columns.
  return <MultiChatRowV3 primary={primarySurface} />;
};

export default MidasChatAreaV3;
